import { AxiosError } from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import apiManager from '@/api/AxiosInstance';
import { postProductInquiry } from '@/api/public';
import BasicFooterButton from '@/components/Common/Button/BasicFooterButton';
import TextInputLabelUp from '@/components/Common/Input/TextInputLabelUp';
import ConfirmModal from '@/components/Common/Modal/ConfirmModal';
import MenuHeader from '@/components/Header/MenuHeader';
import { ALREADY_INTEREST_PRODUCT_NOTIFICATION_SETTINGS } from '@/const/errorCode';
import {
  COMMON_TOAST_ERROR,
  NAME_VALIDATION_LENGTH_MESSAGE,
  NAME_VALIDATION_MESSAGE,
  PHONE_NUMBER_VALIDATION_LENGTH_MESSAGE,
  TONS_IS_LESS_THAN_27,
  TONS_IS_OVER_1,
} from '@/const/errorMessage';
import { positiveFloatRegex } from '@/const/regex';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { useMemberContext } from '@/contexts/Member/MemberContext';
import { formatValueToDecimalPlaces, getCurrentYear } from '@/utils/common';
import { Validation, validateName, validatePhoneNumber } from '@/utils/validation';

type StepKey = 'loaded' | 'tons' | 'year' | 'loadedInnerLength' | 'contact';

interface RangeOption {
  code: string;
  label: string;
  description?: string;
  min: number | null;
  max: number | null;
}

const ETC_TONS_CODE = 'ETC';
const MIN_TONS = 1;
const MAX_TONS = 27;
const MIN_YEAR = 2000;

const TONS_OPTIONS: RangeOption[] = [
  { code: 'TONS_1', label: '1톤', min: 1, max: 2.9 },
  { code: 'TONS_3', label: '3톤', min: 3, max: 4.9 },
  { code: 'TONS_5', label: '5톤 이상', min: 5, max: 13.9 },
  { code: 'TONS_14', label: '14톤 이상', min: 14, max: 20.9 },
  { code: 'TONS_21', label: '21톤 이상', min: 21, max: MAX_TONS },
  { code: ETC_TONS_CODE, label: '기타', min: null, max: null },
];

// 연식은 올해 출고 차량을 1년으로 계산
const getYearOptions = (currentYear: number): RangeOption[] => [
  { code: 'WITHIN_3', label: '3년 이내 신차급', min: currentYear - 2, max: currentYear + 1 },
  { code: 'WITHIN_8', label: '8년 인기 연식', min: currentYear - 7, max: currentYear - 3 },
  { code: 'WITHIN_14', label: '10년 된 가성비 연식', min: currentYear - 13, max: currentYear - 8 },
  { code: 'OVER_15', label: '15년 이상 저렴한 차량', min: MIN_YEAR, max: currentYear - 14 },
];

const LOADED_INNER_LENGTH_OPTIONS: RangeOption[] = [
  { code: 'UNDER_3', label: '3.0m 미만', description: '일반적인 1톤 화물 운송에 적합', min: null, max: 2.9 },
  { code: 'FROM_3_TO_5', label: '3.0m ~ 5.0m', description: '중소형 화물에 적합', min: 3, max: 4.9 },
  { code: 'FROM_5_TO_10', label: '5.0m ~ 10m', description: '긴 화물도 여유롭게 적재', min: 5, max: 9.9 },
  { code: 'OVER_10', label: '10m 이상', description: '부피가 크거나 긴 화물에 적합', min: 10, max: null },
  { code: 'UNKNOWN', label: '잘 모르겠어요', description: '운송할 화물에 맞춰 추천해 드려요', min: null, max: null },
];

const getNameErrorMessage = (name: string) => {
  if (name.length > 20) {
    return NAME_VALIDATION_LENGTH_MESSAGE;
  }
  if (!validateName(name)) {
    return NAME_VALIDATION_MESSAGE;
  }
  return '';
};

const getPhoneNumberErrorMessage = (phoneNumber: string) => {
  if (phoneNumber.length > 11) {
    return PHONE_NUMBER_VALIDATION_LENGTH_MESSAGE;
  }
  const phoneNumberValidation = validatePhoneNumber(phoneNumber) as Validation;
  return phoneNumberValidation.isValid ? '' : phoneNumberValidation.errorMessage;
};

const getEtcTonsErrorMessage = (value: string) => {
  if (!value) {
    return '';
  }
  if (Number(value) < MIN_TONS) {
    return TONS_IS_OVER_1;
  }
  if (Number(value) > MAX_TONS) {
    return TONS_IS_LESS_THAN_27;
  }
  return '';
};

const ProductsPurchasingInquiry = () => {
  const navigate = useNavigate();
  const currentYear = getCurrentYear();
  const yearOptions = useMemo(() => getYearOptions(currentYear), [currentYear]);

  const isClicked = useRef<boolean>(false);
  const { showToast } = useToastContext();
  const { member } = useMemberContext();

  const [stepIndex, setStepIndex] = useState(0);
  const [loadedOptions, setLoadedOptions] = useState<EnumPresenter[]>([]);

  const [selectedLoaded, setSelectedLoaded] = useState<EnumPresenter | null>(null);
  const [selectedTonsCode, setSelectedTonsCode] = useState('');
  const [etcTons, setEtcTons] = useState('');
  const [selectedYearCode, setSelectedYearCode] = useState('');
  const [selectedLoadedInnerLengthCode, setSelectedLoadedInnerLengthCode] = useState('');

  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [nameErrorMessage, setNameErrorMessage] = useState('');
  const [phoneNumberErrorMessage, setPhoneNumberErrorMessage] = useState('');

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    content: React.ReactNode;
    rightButtonLabel: string;
    onRightButtonClick: () => void;
  }>({
    content: null,
    rightButtonLabel: '',
    onRightButtonClick: () => {},
  });

  const isValidMemberContact =
    !!member?.name &&
    !!member?.phoneNumber &&
    getNameErrorMessage(member.name) === '' &&
    getPhoneNumberErrorMessage(member.phoneNumber) === '';

  const steps: StepKey[] = useMemo(
    () => ['loaded', 'tons', 'year', 'loadedInnerLength', ...(isValidMemberContact ? [] : ['contact' as StepKey])],
    [isValidMemberContact],
  );
  const currentStepIndex = Math.min(stepIndex, steps.length - 1);
  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;

  const etcTonsErrorMessage = getEtcTonsErrorMessage(etcTons);

  useEffect(() => {
    apiManager
      .get('/api/v1/public/products/filter-info')
      .then((response) => {
        const responseData: ProductsFilterInfo = response.data;
        setLoadedOptions(
          responseData.loaded
            .filter((item) => item.loaded.code !== 'WIDEWINGBODY')
            .map((item) => ({ code: item.loaded.code, desc: item.loaded.desc })),
        );
      })
      .catch(() => {
        showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
      });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStepIndex]);

  const getTonsRange = () => {
    if (selectedTonsCode === ETC_TONS_CODE) {
      return { min: Number(etcTons), max: Number(etcTons) };
    }
    const option = TONS_OPTIONS.find((item) => item.code === selectedTonsCode);
    return { min: option?.min ?? null, max: option?.max ?? null };
  };

  const getYearRange = () => {
    const option = yearOptions.find((item) => item.code === selectedYearCode);
    return { min: option?.min ?? null, max: option?.max ?? null };
  };

  const getLoadedInnerLengthRange = () => {
    const option = LOADED_INNER_LENGTH_OPTIONS.find((item) => item.code === selectedLoadedInnerLengthCode);
    return { min: option?.min ?? null, max: option?.max ?? null };
  };

  const isStepCompleted = (step: StepKey) => {
    switch (step) {
      case 'loaded':
        return !!selectedLoaded?.code;
      case 'tons':
        if (selectedTonsCode === ETC_TONS_CODE) {
          return !!etcTons && etcTonsErrorMessage === '';
        }
        return !!selectedTonsCode;
      case 'year':
        return !!selectedYearCode;
      case 'loadedInnerLength':
        return !!selectedLoadedInnerLengthCode;
      case 'contact':
        return !!name && !!phoneNumber && nameErrorMessage === '' && phoneNumberErrorMessage === '';
    }
  };

  const onClickPrevBtn = () => {
    if (currentStepIndex === 0) {
      navigate(-1);
      return;
    }
    setStepIndex(currentStepIndex - 1);
  };

  const onClickNextBtn = () => {
    if (!isStepCompleted(currentStep)) {
      return;
    }
    if (isLastStep) {
      createProductInquiry();
      return;
    }
    setStepIndex(currentStepIndex + 1);
  };

  const onChangeEtcTons = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatValueToDecimalPlaces(e.target.value, 1).trim();
    if (formattedValue === '' || positiveFloatRegex.test(formattedValue)) {
      setEtcTons(formattedValue);
    }
  };

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setName(value);
    setNameErrorMessage(getNameErrorMessage(value));
  };

  const onChangePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setPhoneNumber(value);
    setPhoneNumberErrorMessage(getPhoneNumberErrorMessage(value));
  };

  const handleCloseModal = () => {
    setIsOpenModal(false);
    navigate('/');
  };

  const onClickRegisterNotification = () => {
    const tonsRange = getTonsRange();
    const yearRange = getYearRange();
    const loadedInnerLengthRange = getLoadedInnerLengthRange();

    apiManager
      .post(`/api/v1/interest-product-notification-settings`, {
        loaded: selectedLoaded?.code,
        minTons: tonsRange.min,
        maxTons: tonsRange.max,
        minYear: yearRange.min,
        maxYear: yearRange.max,
        minLoadedInnerLength: loadedInnerLengthRange.min,
        maxLoadedInnerLength: loadedInnerLengthRange.max,
      })
      .then(() => {
        showToast('차량 입고 알림이 등록되었어요.', 'success', 'bottom');
      })
      .catch((error) => {
        if (error && error.code === ALREADY_INTEREST_PRODUCT_NOTIFICATION_SETTINGS) {
          showToast(error.message, 'error', 'bottom');
        } else {
          showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
        }
      });

    handleCloseModal();
  };

  const postProductPurchasingInquiryMutation = useMutation(
    (request: ProductPurchasingInquiryRequest) => postProductInquiry(request),
    {
      onSuccess: (response) => {
        if (!response) {
          return;
        }
        if (member?.id) {
          setModalConfig({
            content: (
              <span>
                알림 받기를 누르면 해당 차량이
                <br />
                입고되었을 때 알림을 보내드려요.
              </span>
            ),
            rightButtonLabel: '알림받기',
            onRightButtonClick: onClickRegisterNotification,
          });
        } else {
          setModalConfig({
            content: (
              <span className="font-medium text-blue-8">
                알림을 받으려면 로그인이 필요해요.
                <br />
                지금 바로 로그인하고,
                <br />
                원하는 차량 정보를 받아보세요!
              </span>
            ),
            rightButtonLabel: '로그인하기',
            onRightButtonClick: () => navigate('/login'),
          });
        }
        setIsOpenModal(true);
      },
      onError: (error: AxiosError) => {
        isClicked.current = false;
        if (error && error.code === 'BAD_REQUEST') {
          showToast('필수값이 존재하지 않습니다.', 'error', 'bottom');
        } else {
          showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
        }
      },
    },
  );

  const createProductInquiry = () => {
    if (isClicked.current === true) {
      return;
    }
    isClicked.current = true;

    const tonsRange = getTonsRange();
    const yearRange = getYearRange();
    const loadedInnerLengthRange = getLoadedInnerLengthRange();

    postProductPurchasingInquiryMutation.mutate({
      memberId: member?.id,
      name: isValidMemberContact ? member?.name || '' : name,
      requestPhoneNumber: isValidMemberContact ? member?.phoneNumber || '' : phoneNumber,
      loaded: selectedLoaded?.code || null,
      minTons: tonsRange.min,
      maxTons: tonsRange.max,
      minYear: yearRange.min,
      maxYear: yearRange.max,
      minLoadedInnerLength: loadedInnerLengthRange.min,
      maxLoadedInnerLength: loadedInnerLengthRange.max,
    });
  };

  const renderStepTitle = (title: React.ReactNode, description?: string) => (
    <div className="mb-6">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-[20px] leading-[28px] font-bold text-gray-9 break-keep">{title}</h2>
        <span className="shrink-0 mt-[2px] px-[10px] py-[2px] rounded-full bg-primary-10 text-white text-[14px] leading-[20px] font-bold">
          {currentStepIndex + 1}/{steps.length}
        </span>
      </div>
      {description && <p className="mt-2 text-[14px] leading-[20px] text-gray-7">{description}</p>}
    </div>
  );

  const getOptionButtonClassName = (isSelected: boolean) =>
    `w-full rounded-[10px] border break-keep ${
      isSelected ? 'border-primary-10 bg-primary-1 text-primary-10 font-semibold' : 'border-gray-3 bg-white text-gray-8'
    }`;

  const renderLoadedStep = () => (
    <>
      {renderStepTitle('어떤 적재함 종류를 찾으시나요?')}
      <div className="grid grid-cols-3 gap-2">
        {loadedOptions.map((item) => (
          <button
            key={item.code}
            type="button"
            onClick={() => setSelectedLoaded(item)}
            aria-pressed={selectedLoaded?.code === item.code}
            className={`h-[48px] px-1 text-[14px] leading-[18px] tracking-tight sm280:text-[12px] ${getOptionButtonClassName(
              selectedLoaded?.code === item.code,
            )}`}
          >
            {item.desc}
          </button>
        ))}
      </div>
    </>
  );

  const renderTonsStep = () => (
    <>
      {renderStepTitle('어떤 톤수를 찾으시나요?')}
      <div className="grid grid-cols-2 gap-2">
        {TONS_OPTIONS.map((item) => (
          <button
            key={item.code}
            type="button"
            onClick={() => setSelectedTonsCode(item.code)}
            aria-pressed={selectedTonsCode === item.code}
            className={`h-[56px] text-[16px] ${getOptionButtonClassName(selectedTonsCode === item.code)}`}
          >
            {item.label}
          </button>
        ))}
      </div>
      {selectedTonsCode === ETC_TONS_CODE && (
        <div className="mt-5">
          <p className="mb-2 text-[14px] text-gray-8">원하시는 톤수를 입력해 주세요</p>
          <div className="relative">
            <input
              className={`w-full h-[48px] px-4 pr-10 rounded-[10px] border text-[16px] text-gray-9 outline-none ${
                etcTonsErrorMessage ? 'border-red' : 'border-gray-3 focus:border-primary-10'
              }`}
              placeholder="예: 2.5"
              value={etcTons}
              onChange={onChangeEtcTons}
              inputMode="decimal"
              maxLength={4}
              autoFocus
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[16px] text-gray-7">톤</span>
          </div>
          {etcTonsErrorMessage && <p className="mt-1 text-[13px] text-red">{etcTonsErrorMessage}</p>}
        </div>
      )}
    </>
  );

  const renderYearStep = () => (
    <>
      {renderStepTitle('선호하시는 차량 연식을 알려주세요.')}
      <div className="flex flex-col gap-2">
        {yearOptions.map((item) => (
          <button
            key={item.code}
            type="button"
            onClick={() => setSelectedYearCode(item.code)}
            aria-pressed={selectedYearCode === item.code}
            className={`h-[56px] text-[16px] ${getOptionButtonClassName(selectedYearCode === item.code)}`}
          >
            {item.label}
          </button>
        ))}
      </div>
    </>
  );

  const renderLoadedInnerLengthStep = () => (
    <>
      {renderStepTitle('선호하시는 적재함 길이를 알려주세요.', '적재함 안쪽 바닥의 길이를 기준으로 선택해 주세요.')}
      <div className="flex flex-col gap-2">
        {LOADED_INNER_LENGTH_OPTIONS.map((item) => {
          const isSelected = selectedLoadedInnerLengthCode === item.code;
          return (
            <button
              key={item.code}
              type="button"
              onClick={() => setSelectedLoadedInnerLengthCode(item.code)}
              aria-pressed={isSelected}
              className={`flex items-center justify-between px-4 py-3 text-left ${getOptionButtonClassName(
                isSelected,
              )}`}
            >
              <div>
                <p className="text-[16px] leading-[22px] font-semibold text-gray-9">{item.label}</p>
                <p className="mt-[2px] text-[12px] leading-[16px] text-gray-7 font-normal">{item.description}</p>
              </div>
              <span
                className={`flex items-center justify-center shrink-0 w-5 h-5 rounded-full border-2 ${
                  isSelected ? 'border-primary-10' : 'border-gray-4'
                }`}
              >
                {isSelected && <span className="w-[10px] h-[10px] rounded-full bg-primary-10" />}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );

  const renderContactStep = () => (
    <>
      {renderStepTitle('연락받으실 정보를 알려주세요.', '입력하신 번호로 빠르게 상담 연락을 드려요.')}
      <div className="flex flex-col gap-[30px]">
        <TextInputLabelUp
          name="name"
          label="신청자명"
          placeholder="신청자명을 입력해주세요"
          value={name}
          onChange={onChangeName}
          type="text"
          error={!!nameErrorMessage}
          errorMsg={nameErrorMessage}
          required={true}
        ></TextInputLabelUp>

        <TextInputLabelUp
          name="requestPhoneNumber"
          label="휴대폰 번호"
          placeholder="휴대폰 번호를 입력해주세요"
          value={phoneNumber}
          onChange={onChangePhoneNumber}
          inputMode="numeric"
          error={!!phoneNumberErrorMessage}
          errorMsg={phoneNumberErrorMessage}
          required={true}
        ></TextInputLabelUp>
      </div>
    </>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 'loaded':
        return renderLoadedStep();
      case 'tons':
        return renderTonsStep();
      case 'year':
        return renderYearStep();
      case 'loadedInnerLength':
        return renderLoadedInnerLengthStep();
      case 'contact':
        return renderContactStep();
    }
  };

  return (
    <>
      <MenuHeader title="차량 구매문의" onClickPrevBtn={onClickPrevBtn}></MenuHeader>
      <div className="w-full px-4 pt-[80px] pb-[100px]">{renderStep()}</div>
      <BasicFooterButton
        text={isLastStep ? '문의하기' : '다음'}
        onClick={onClickNextBtn}
        disabled={!isStepCompleted(currentStep)}
      />
      <ConfirmModal
        isOpen={isOpenModal}
        title="구매 문의 등록 완료"
        content={modalConfig.content}
        leftButtonLabel="닫기"
        rightButtonLabel={modalConfig.rightButtonLabel}
        onLeftButtonClick={handleCloseModal}
        onRightButtonClick={modalConfig.onRightButtonClick}
      />
    </>
  );
};

export default ProductsPurchasingInquiry;
